import React, { useEffect, useState } from "react";
import { Gender, InputValidationState, NameOptions, Option } from "../../types";
import { useAtom, useAtomValue } from "jotai";
import { formStateAtom, userAtom } from "../../store/store";
import { useUser } from "../../hooks/useUser";
import Card from "../common/Card/Card";
import Input from "../common/Input/Input";
import Button from "../common/Button/Button";
import Accordion from "../common/Accordion/Accordion";
import PaymentModal from "../PaymentModal/PaymentModal";
import "./NameGenerator.css";

interface NameGeneratorProps {
  onGenerate: () => void;
  isGenerating: boolean;
  triesLeft: number;
  onUnlock: () => void;
}

const options = [
  {
    label: "styles",
    value: "风格",
    options: [
      { label: "Modern", value: "现代" },
      { label: "Ancient", value: "古典" },
      { label: "Poetic", value: "诗意" },
      { label: "Simple", value: "简约" },
    ],
  },
  {
    label: "themes",
    value: "主题",
    options: [
      { label: "Nature", value: "自然" },
      { label: "Culture", value: "文化" },
      { label: "Strength", value: "力量" },
      { label: "Beauty", value: "美丽" },
    ],
  },
  {
    label: "meanings",
    value: "寓意",
    options: [
      { label: "Luck", value: "幸运" },
      { label: "Wisdom", value: "智慧" },
      { label: "Prosperity", value: "繁荣" },
      { label: "Harmony", value: "和谐" },
    ],
  },
  {
    label: "phonetics",
    value: "音韵",
    options: [
      { label: "Easy-read", value: "易读" },
      { label: "Rhyme", value: "押韵" },
      { label: "Melodic", value: "悦耳" },
      { label: "Balanced", value: "平衡" },
    ],
  },
];

const NameGenerator: React.FC<NameGeneratorProps> = ({
  onGenerate,
  isGenerating,
  triesLeft,
  onUnlock,
}) => {
  const { openLoginModal } = useUser();
  const [formState, setFormState] = useAtom(formStateAtom);
  const user = useAtomValue(userAtom);
  const [firstNameValidation, setFirstNameValidation] =
    useState<InputValidationState>("default");
  const [lastNameValidation, setLastNameValidation] =
    useState<InputValidationState>("default");
  const isOptionSelected = (category: string, label: string) => {
    const options = formState?.options[
      category as keyof Omit<NameOptions, "length">
    ] as Option[];
    return options.some((option) => option.label === label);
  };
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // 验证姓氏
  const validateLastName = (value: string): boolean => {
    // 检查长度
    if (value.length < 1 || value.length > 10) {
      setLastNameValidation("error");
      return false;
    }

    // 检查是否只包含英文字母
    if (!/^[A-Za-z]+$/.test(value)) {
      setLastNameValidation("error");
      return false;
    }

    setLastNameValidation("success");
    return true;
  };

  // 验证名字
  const validateFirstName = (value: string): boolean => {
    // 检查长度
    if (value.length < 1 || value.length > 20) {
      setFirstNameValidation("error");
      return false;
    }

    // 检查是否只包含英文字母
    if (!/^[A-Za-z]+$/.test(value)) {
      setFirstNameValidation("error");
      return false;
    }

    setFirstNameValidation("success");
    return true;
  };

  // 处理姓氏变化
  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();

    // 保存表单状态到全局状态
    updateFormState(
      value,
      formState.firstName,
      formState.gender,
      formState.length
    );
  };

  // 处理名字变化
  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();

    // 保存表单状态到全局状态
    updateFormState(
      formState.lastName,
      value,
      formState.gender,
      formState.length
    );
  };

  // 更新表单状态函数
  const updateFormState = (
    lastName: string,
    firstName: string,
    gender: Gender,
    length: number
  ) => {
    setFormState((prev) => ({
      ...prev,
      lastName,
      firstName,
      gender,
      length,
    }));
  };

  const handleOptionChange = (category: keyof NameOptions, value: any) => {
    setFormState((prev) => {
      // 处理多选项
      const currentValues = [...prev.options[category]];
      const valueIndex = currentValues.findIndex(
        (item) => item.label === value.label
      );
      if (valueIndex === -1) {
        currentValues.push(value);
      } else {
        currentValues.splice(valueIndex, 1);
      }

      return {
        ...prev,
        options: { ...prev.options, [category]: currentValues },
      };
    });
  };

  const handleSubmit = () => {
    if (!user.isLogin) {
      openLoginModal();

      return;
    }

    const isLastNameValid = validateLastName(formState.lastName);
    const isFirstNameValid = validateFirstName(formState.firstName);

    if (isLastNameValid && isFirstNameValid) {
      onGenerate();
    }
  };

  const handleUnlockClick = () => {
    setShowPaymentModal(true);
  };

  const isButtonDisabled = isGenerating || triesLeft <= 0;

  useEffect(() => {
    setFirstNameValidation("default");
    setLastNameValidation("default");
  }, [formState.firstName, formState.lastName]);

  return (
    <Card className="name-generator-card">
      <div className="name-input-section">
        <div className="name-inputs-row">
          <Input
            label="First Name"
            value={formState.firstName}
            onChange={handleFirstNameChange}
            placeholder="First Name"
            validationState={firstNameValidation}
            errorMessage="First name must be 1-20 English letters only"
            onFocus={() => {
              setFirstNameValidation("default");
            }}
            required
            className="first-name-input"
          />
          <Input
            label="Last Name"
            value={formState.lastName}
            onChange={handleLastNameChange}
            placeholder="Last Name"
            validationState={lastNameValidation}
            errorMessage="Last name must be 1-10 English letters only"
            onFocus={() => {
              setLastNameValidation("default");
            }}
            required
            className="last-name-input"
          />
        </div>
        <div className="gender-selection">
          <p className="gender-label">Gender</p>
          <div className="gender-radio-group">
            <label className="gender-radio-option">
              <input
                type="radio"
                name="gender"
                value="male"
                checked={formState.gender === "male"}
                onChange={() => {
                  updateFormState(
                    formState.lastName,
                    formState.firstName,
                    "male",
                    formState.length
                  );
                }}
              />
              <span>Male</span>
            </label>
            <label className="gender-radio-option">
              <input
                type="radio"
                name="gender"
                value="female"
                checked={formState.gender === "female"}
                onChange={() => {
                  updateFormState(
                    formState.lastName,
                    formState.firstName,
                    "female",
                    formState.length
                  );
                }}
              />
              <span>Female</span>
            </label>
            <label className="gender-radio-option">
              <input
                type="radio"
                name="gender"
                value="neutral"
                checked={formState.gender === "neutral"}
                onChange={() => {
                  updateFormState(
                    formState.lastName,
                    formState.firstName,
                    "neutral",
                    formState.length
                  );
                }}
              />
              <span>Neutral</span>
            </label>
          </div>
        </div>
      </div>

      <Accordion
        title="Personalization Options"
        subtitle="Personalization options are for reference only"
        defaultOpen
      >
        <div className="options-container">
          <div className="option-group">
            <h4>Character Count</h4>
            <div className="radio-group">
              {[2, 3, 4].map((length) => (
                <label key={length} className="radio-option">
                  <input
                    type="radio"
                    name="length"
                    checked={formState.length === length}
                    onChange={() =>
                      updateFormState(
                        formState.lastName,
                        formState.firstName,
                        formState.gender,
                        length
                      )
                    }
                  />
                  <span>{length} Characters</span>
                </label>
              ))}
            </div>
          </div>

          {/* 使用 for 循环遍历 options 数组 */}
          {options.map((optionGroup) => (
            <div key={optionGroup.label} className="option-group">
              <h4>{optionGroup.label}</h4>
              <div className="checkbox-group">
                {optionGroup.options.map((item) => {
                  return (
                    <label key={item.label} className="checkbox-option">
                      <input
                        type="checkbox"
                        checked={isOptionSelected(
                          optionGroup.label,
                          item.label
                        )}
                        onChange={() =>
                          handleOptionChange(
                            optionGroup.label as keyof Omit<
                              NameOptions,
                              "length"
                            >,
                            item
                          )
                        }
                      />
                      <span>{item.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Accordion>

      <div className="generate-section">
        <Button
          variant="primary"
          size="lg"
          state={
            isGenerating ? "loading" : isButtonDisabled ? "disabled" : "default"
          }
          onClick={handleSubmit}
          fullWidth
        >
          Design Chinese Name
        </Button>

        <div className="free-tries-info">
          {triesLeft > 0 ? (
            <p>You have {triesLeft} free tries left</p>
          ) : (
            <div className="unlock-section">
              <p>
                Free attempts exhausted. Unlock 99 creative designs for this
                real name
              </p>
              <Button
                variant="primary"
                size="md"
                onClick={handleUnlockClick}
                className="premium-unlock-button"
                fullWidth
              >
                <span className="shine-effect"></span>
                <span className="unlock-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19 11H5C3.89543 11 3 11.8954 3 13V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V13C21 11.8954 20.1046 11 19 11Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7 11V7C7 5.93913 7.42143 4.92172 8.17157 4.17157C8.92172 3.42143 9.93913 3 11 3H13C14.0609 3 15.0783 3.42143 15.8284 4.17157C16.5786 4.92172 17 5.93913 17 7V11"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 16C12.5523 16 13 15.5523 13 15C13 14.4477 12.5523 14 12 14C11.4477 14 11 14.4477 11 15C11 15.5523 11.4477 16 12 16Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                Unlock 99 Designs for $1.99
              </Button>
            </div>
          )}
        </div>
      </div>

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={onUnlock}
        firstName={formState.firstName}
        lastName={formState.lastName}
      />
    </Card>
  );
};

export default NameGenerator;
